import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CartItem } from "../../../models/cart-item.ts";
import { CategoryType } from "../../../models/category-type.ts";
import { Product as ProductType } from "../../../models/product.ts";
import { SizeOption } from "../../../models/size-option.ts";
import {
  getProductById,
  getProducts,
} from "../../../Services/product.service.ts";
import { addToCart } from "../../../store/actions/cartActions.ts";
import type { RootState } from "../../../store/store.ts";
import BarSpinner from "../../Loader/BarSpinner.tsx";
import CardRating from "../CardRating.tsx";
import ProductReviews from "../reviews/ProductReviews.tsx";
import "./Product.css";

const PinchZoom = ({ src, alt }: { src: string; alt: string }) => {
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLDivElement>(null);
  const initialDistance = useRef<number>(0);
  const initialScale = useRef<number>(1);
  const zoomedRef = useRef(false);
  zoomedRef.current = scale > 1;

  const resetZoom = () => {
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
  };

  useEffect(() => {
    const node = imageRef.current;
    if (!node) return;
    // React touch listeners are passive, so preventDefault needs a native one.
    const stopPageGesture = (event: TouchEvent) => {
      if (!event.cancelable) return;
      if (event.touches.length > 1 || zoomedRef.current) event.preventDefault();
    };
    node.addEventListener("touchmove", stopPageGesture, { passive: false });
    return () => node.removeEventListener("touchmove", stopPageGesture);
  }, []);

  useEffect(() => {
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
  }, [src]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      initialDistance.current = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY,
      );
      initialScale.current = scale;
    } else if (e.touches.length === 1) {
      setIsDragging(true);
      setLastPosition({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY,
      );
      const next = Math.max(
        1,
        Math.min(3, (distance / initialDistance.current) * initialScale.current),
      );
      setScale(next);
      if (next === 1) {
        setTranslateX(0);
        setTranslateY(0);
      }
    } else if (e.touches.length === 1 && isDragging && scale > 1) {
      const deltaX = e.touches[0].clientX - lastPosition.x;
      const deltaY = e.touches[0].clientY - lastPosition.y;
      setTranslateX((prev) => prev + deltaX);
      setTranslateY((prev) => prev + deltaY);
      setLastPosition({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  };

  return (
    <div className="pinch-zoom-container">
      <div
        ref={imageRef}
        className="pinch-zoom-main"
        style={{ touchAction: scale > 1 ? "none" : "pan-x pan-y" }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={() => setIsDragging(false)}
        onDoubleClick={() => (scale > 1 ? resetZoom() : setScale(2))}
      >
        <img
          src={src}
          alt={alt}
          style={{
            transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
            transformOrigin: "center",
            transition: isDragging ? "none" : "transform 0.1s ease",
          }}
        />
        {scale > 1 && (
          <button className="reset-zoom-btn" onClick={resetZoom}>
            Reset
          </button>
        )}
      </div>
    </div>
  );
};

const saleAmount = (product?: ProductType | null) =>
  typeof product?.sale === "number" ? product.sale : 0;

const Product = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const guideRef = useRef<HTMLDetailsElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState<ProductType | null>(null);
  const [allProducts, setAllProducts] = useState<ProductType[]>([]);
  const [currentProductIndex, setCurrentProductIndex] = useState(-1);
  const [quantity, setQuantity] = useState(1);
  const [currentImg, setCurrentImg] = useState(0);
  const [selectSize, setSelectSize] = useState<SizeOption | null | undefined>(
    null,
  );
  const [highlightedColor, setHighlightedColor] = useState("");
  const [sizeError, setSizeError] = useState("");
  const [shake, setShake] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const [showBar, setShowBar] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia("(max-width: 900px)").matches,
  );
  const [reviewSummary, setReviewSummary] = useState({ average: 0, count: 0 });
  const onReviewSummary = useCallback(
    (summary: { average: number; count: number }) => setReviewSummary(summary),
    [],
  );

  useEffect(() => {
    const media = window.matchMedia("(max-width: 900px)");
    const sync = () => setIsMobile(media.matches);
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setQuantity(1);
      setCurrentImg(0);
      setSelectSize(null);
      setHighlightedColor("");
      setSizeError("");
      setZoomed(false);
      if (!id) {
        setProduct(null);
        setLoading(false);
        return;
      }
      const result = await getProductById(id);
      if (result.success) {
        setProduct(result.data as ProductType);
      } else {
        setProduct(null);
      }
      const allProductsResult = await getProducts();
      if (allProductsResult.success) {
        const fetchedProducts = allProductsResult.data as ProductType[];
        setAllProducts(fetchedProducts);
        setCurrentProductIndex(fetchedProducts.findIndex((item) => item.id === id));
      }
      setLoading(false);
    };
    fetchProduct();
  }, [id]);

  const productImg = product
    ? [
        product.images?.front || "",
        product.images?.back || "",
        ...(product.images?.detail || []),
      ].filter(Boolean)
    : [];

  useEffect(() => {
    const button = addRef.current;
    if (!button || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      setShowBar(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    observer.observe(button);
    return () => observer.disconnect();
  }, [product]);

  const cartItems = useSelector(
    (state: RootState) => state.cart.items,
  ) as CartItem[];

  const addCurrentProduct = () => {
    if (!product) return false;
    if (product.isOutOfStock) {
      toast.error("This product is currently sold out");
      return false;
    }
    const isUnstitched = product.category?.name === CategoryType.UNSTITCHED;
    if (!isUnstitched && !selectSize) {
      setSizeError("Please select a size");
      setShake(true);
      window.setTimeout(() => setShake(false), 350);
      toast.error("Please select a size");
      return false;
    }
    if (!highlightedColor) {
      toast.error("Please select a color");
      return false;
    }
    const uniqueId = `${product.id}_${selectSize || "no-size"}_${highlightedColor}`;
    const productItem: CartItem = {
      productID: product.id ?? "",
      productName: product.name,
      quantity,
      productPrice: product.price,
      frontImg: product.images.front ?? "",
      id: uniqueId,
      color: highlightedColor,
      size: isUnstitched ? "No Size" : (selectSize ?? ""),
      sale: saleAmount(product),
    };
    const productInCart = cartItems.find((item) => item.id === uniqueId);
    if (productInCart && (productInCart.quantity ?? 0) >= 20) {
      toast.error("Product limit reached");
      return false;
    }
    dispatch(addToCart(productItem));
    toast.success("Added to cart!");
    return true;
  };

  if (loading) {
    return (
      <div className="az productLoading">
        <div className="loader-wrap">
          <BarSpinner />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="az">
        <div className="empty">Product not found.</div>
      </div>
    );
  }

  const sale = saleAmount(product);
  const finalPrice =
    sale > 0 ? Math.round(product.price * (1 - sale / 100)) : product.price;
  const isUnstitched = product.category?.name === CategoryType.UNSTITCHED;
  const colors = (product.stockOptions || [])
    .map((option) => option.color)
    .filter(
      (color, index, list) => list.findIndex((item) => item.hex === color.hex) === index,
    );
  const sizes = (product.stockOptions || [])
    .map((option) => option.size)
    .filter((size, index, list) => size && list.indexOf(size) === index);
  const activeColor =
    colors.find((color) => color.hex === highlightedColor) || colors[0];
  const related = allProducts
    .filter((item) => item.id !== product.id)
    .sort(
      (a, b) =>
        Number(b.category?.name === product.category?.name) -
        Number(a.category?.name === product.category?.name),
    )
    .slice(0, 4);

  return (
    <div className="az">
      <main className="az-wrap">
        <div className="crumbs pdp-pad">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/shop">Shop All</Link>
          <span>/</span>
          <span>{product.name}</span>
          <span style={{ marginLeft: "auto", display: "flex", gap: 12 }}>
            <button
              className="linkbtn"
              disabled={currentProductIndex <= 0}
              onClick={() =>
                currentProductIndex > 0 &&
                navigate(`/product/${allProducts[currentProductIndex - 1].id}`)
              }
            >
              Prev
            </button>
            <button
              className="linkbtn"
              disabled={
                currentProductIndex < 0 ||
                currentProductIndex >= allProducts.length - 1
              }
              onClick={() =>
                currentProductIndex < allProducts.length - 1 &&
                navigate(`/product/${allProducts[currentProductIndex + 1].id}`)
              }
            >
              Next
            </button>
          </span>
        </div>
        <div className="pdp">
          <div className={`gal pdp-bleed${productImg.length < 2 ? " one" : ""}`}>
            <div className="thumbs">
              {productImg.map((img, index) => (
                <button
                  key={img}
                  className={index === currentImg ? "on" : ""}
                  onClick={() => {
                    setCurrentImg(index);
                    setZoomed(false);
                  }}
                  aria-label={`Show image ${index + 1}`}
                >
                  <img src={img} alt="" />
                </button>
              ))}
            </div>
            <div className="stage">
              <div
                className={`gslide${zoomed ? " zoom" : ""}`}
                onClick={() => {
                  if (!isMobile) setZoomed((open) => !open);
                }}
                onMouseMove={(event) => {
                  if (!zoomed) return;
                  const rect = event.currentTarget.getBoundingClientRect();
                  setOrigin(
                    `${((event.clientX - rect.left) / rect.width) * 100}% ${
                      ((event.clientY - rect.top) / rect.height) * 100
                    }%`,
                  );
                }}
                onMouseLeave={() => setZoomed(false)}
              >
                {isMobile ? (
                  <PinchZoom src={productImg[currentImg]} alt={product.name} />
                ) : (
                  <img
                    src={productImg[currentImg]}
                    alt={product.name}
                    style={{ transformOrigin: origin }}
                  />
                )}
                {(product.isOutOfStock || sale > 0) && (
                  <span className="tag">
                    {product.isOutOfStock ? "Sold Out" : `-${sale}%`}
                  </span>
                )}
              </div>
              {productImg.length > 1 && (
                <>
                  <button
                    className="gnav gprev"
                    aria-label="Previous image"
                    onClick={() =>
                      setCurrentImg((index) =>
                        index === 0 ? productImg.length - 1 : index - 1,
                      )
                    }
                  >
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M10 3L5 8l5 5" />
                    </svg>
                  </button>
                  <button
                    className="gnav gnext"
                    aria-label="Next image"
                    onClick={() =>
                      setCurrentImg((index) =>
                        index === productImg.length - 1 ? 0 : index + 1,
                      )
                    }
                  >
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M6 3l5 5-5 5" />
                    </svg>
                  </button>
                  <div className="gdots">
                    {productImg.map((img, index) => (
                      <button
                        key={img}
                        className={index === currentImg ? "on" : ""}
                        aria-label={`Show image ${index + 1}`}
                        onClick={() => setCurrentImg(index)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="pinfo pdp-pad">
            {product.category?.name && (
              <span className="ptag">{product.category.name}</span>
            )}
            <h1>{product.name}</h1>
            <div className="pprice">
              Rs {finalPrice.toLocaleString()}
              {sale > 0 && <s>Rs {product.price.toLocaleString()}</s>}
            </div>
            <button
              type="button"
              className="ratingLine"
              onClick={() =>
                document.getElementById("reviews")?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                })
              }
            >
              <span className="stars" aria-hidden="true">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i
                    key={star}
                    className={star <= Math.round(reviewSummary.average) ? "on" : ""}
                  >
                    ★
                  </i>
                ))}
              </span>
              <span>
                {reviewSummary.count === 0
                  ? "No reviews yet"
                  : `${reviewSummary.average.toFixed(1)} · ${reviewSummary.count} ${
                      reviewSummary.count === 1 ? "review" : "reviews"
                    }`}
              </span>
            </button>
            <div className="pmeta">
              {activeColor?.hex && (
                <span className="d" style={{ background: activeColor.hex }} />
              )}
              {activeColor?.name}
              {product.type && <span>/ {product.type}</span>}
            </div>
            {colors.length > 0 && (
              <>
                <div className="prow">
                  <b>
                    Colour{" "}
                    <span>{activeColor && highlightedColor ? `— ${activeColor.name}` : ""}</span>
                  </b>
                </div>
                <div className="color-row">
                  {colors.map((color) => (
                    <Tooltip
                      key={color.hex}
                      title={color.name}
                      placement="top"
                      enterTouchDelay={0}
                      TransitionComponent={Zoom}
                      arrow
                    >
                      <button
                        className={`color-pick${highlightedColor === color.hex ? " on" : ""}`}
                        style={{ ["--swatch" as string]: color.hex }}
                        aria-label={color.name}
                        onClick={() => setHighlightedColor(color.hex)}
                      />
                    </Tooltip>
                  ))}
                </div>
              </>
            )}
            {!isUnstitched && (
              <>
                <div className="prow">
                  <b>
                    Size <span>{selectSize ? `— ${selectSize}` : ""}</span>
                  </b>
                  <button
                    className="linkbtn"
                    onClick={() => {
                      if (guideRef.current) {
                        guideRef.current.open = true;
                        guideRef.current.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        });
                      }
                    }}
                  >
                    Size guide
                  </button>
                </div>
                <div className={`sizes${shake ? " shake" : ""}`}>
                  {sizes.map((size) => (
                    <button
                      key={String(size)}
                      className={selectSize === size ? "on" : ""}
                      onClick={() => {
                        setSelectSize(size);
                        setSizeError("");
                      }}
                    >
                      {size}
                    </button>
                  ))}
                </div>
                {sizeError && <div className="serr">{sizeError}</div>}
              </>
            )}
            <div className="buy">
              <div className="qty">
                <button
                  aria-label="Decrease quantity"
                  onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                >
                  −
                </button>
                <input
                  aria-label="Quantity"
                  value={quantity}
                  onChange={(event) => {
                    const value = parseInt(event.target.value, 10);
                    if (!isNaN(value) && value > 0) setQuantity(value);
                  }}
                />
                <button
                  aria-label="Increase quantity"
                  onClick={() => setQuantity((value) => value + 1)}
                >
                  +
                </button>
              </div>
              <button
                ref={addRef}
                className="cta dark"
                disabled={product.isOutOfStock}
                onClick={addCurrentProduct}
              >
                {product.isOutOfStock ? "Sold Out" : "Add to bag"}
              </button>
            </div>
            <button
              className="cta line"
              disabled={product.isOutOfStock}
              onClick={() => {
                if (addCurrentProduct()) navigate("/cart");
              }}
            >
              Buy it now
            </button>
            <div className="perks">
              <div className="perk">Stitched and ready to wear</div>
              <div className="perk">Easy exchange on every order</div>
            </div>
            <div className="acc">
              <details open>
                <summary>Design details</summary>
                <div className="ab">
                  <p>{product.description}</p>
                </div>
              </details>
              <details>
                <summary>What's included</summary>
                <div className="ab">
                  <dl className="spec">
                    <dt>Design</dt>
                    <dd>{product.design || "—"}</dd>
                    <dt>Fabric</dt>
                    <dd>{product.fabric || "—"}</dd>
                    <dt>Type</dt>
                    <dd>{product.type || "—"}</dd>
                    <dt>Sale</dt>
                    <dd>{sale > 0 ? `${sale}%` : "—"}</dd>
                  </dl>
                </div>
              </details>
              <details ref={guideRef}>
                <summary>Size guide</summary>
                <div className="ab">
                  {product.sizeChart ? (
                    <img
                      className="sizeChart"
                      src={product.sizeChart}
                      alt={`${product.name} size chart`}
                    />
                  ) : (
                  <>
                  <table className="stable">
                    <thead>
                      <tr>
                        <th>Shirt</th>
                        <th>XS</th>
                        <th>S</th>
                        <th>M</th>
                        <th>L</th>
                        <th>XL</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Length</td>
                        <td>34</td>
                        <td>34</td>
                        <td>34</td>
                        <td>34</td>
                        <td>34</td>
                      </tr>
                      <tr>
                        <td>Chest</td>
                        <td>19</td>
                        <td>20</td>
                        <td>21</td>
                        <td>22</td>
                        <td>24</td>
                      </tr>
                      <tr>
                        <td>Shoulder</td>
                        <td>16</td>
                        <td>16</td>
                        <td>17</td>
                        <td>18</td>
                        <td>19</td>
                      </tr>
                      <tr>
                        <td>Sleeves</td>
                        <td>20</td>
                        <td>21</td>
                        <td>22</td>
                        <td>22</td>
                        <td>22</td>
                      </tr>
                    </tbody>
                  </table>
                  <table className="stable">
                    <thead>
                      <tr>
                        <th>Trouser</th>
                        <th>XS</th>
                        <th>S</th>
                        <th>M</th>
                        <th>L</th>
                        <th>XL</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Length</td>
                        <td>38</td>
                        <td>38</td>
                        <td>38</td>
                        <td>38</td>
                        <td>38</td>
                      </tr>
                      <tr>
                        <td>Waist</td>
                        <td>26-28</td>
                        <td>28-30</td>
                        <td>30-32</td>
                        <td>32-34</td>
                        <td>34-38</td>
                      </tr>
                      <tr>
                        <td>Hips</td>
                        <td>38</td>
                        <td>42</td>
                        <td>43</td>
                        <td>45</td>
                        <td>48</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="note">
                    Measurements are of the garment. Between sizes? Pick the larger one.
                  </div>
                  </>
                  )}
                </div>
              </details>
              <details>
                <summary>Exchange & care</summary>
                <div className="ab">
                  <p>
                    Exchanges are accepted on unworn pieces with tags attached.
                  </p>
                  <p>
                    Dry clean recommended for embroidered shawls. Iron the suit on
                    low heat, inside out.
                  </p>
                </div>
              </details>
            </div>
          </div>
        </div>
        <ProductReviews productId={product.id || id || ""} onSummary={onReviewSummary} />
        {related.length > 0 && (
          <section className="rel">
            <h2>
              You may also like <Link to="/shop">View all</Link>
            </h2>
            <div className="rgrid">
              {related.map((item) => {
                const itemSale = saleAmount(item);
                const itemPrice =
                  itemSale > 0
                    ? Math.round(item.price * (1 - itemSale / 100))
                    : item.price;
                return (
                  <Link className="card" to={`/product/${item.id}`} key={item.id}>
                    <div className="ph">
                      <img src={item.images.front} alt={item.name} />
                      {item.images.back && (
                        <img className="h" src={item.images.back} alt="" />
                      )}
                    </div>
                    <h3>{item.name}</h3>
                    <div className="p">
                      Rs {itemPrice.toLocaleString()}
                      <CardRating
                        average={item.ratingAverage}
                        count={item.ratingCount}
                      />
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </main>
      <div className={`mbar${showBar ? " show" : ""}`}>
        <div>
          <b>{product.name}</b>
          <span>Rs {finalPrice.toLocaleString()}</span>
        </div>
        <button
          className="cta dark"
          disabled={product.isOutOfStock}
          onClick={addCurrentProduct}
        >
          {product.isOutOfStock ? "Sold Out" : "Add to bag"}
        </button>
      </div>
    </div>
  );
};

export default Product;
